import { useEffect, useState } from "react";

/**
 * UsageBadge — shows lifetime report usage vs plan limit.
 *
 * Hidden entirely on local dev (localhost / 127.0.0.1) — no API call made.
 * PRO users see "Unlimited plan".
 * FREE users see "X of 3 reports used" with a bar + upgrade prompt when near/at limit.
 *
 * Props: userId (string)
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const isLocalDev =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

export default function UsageBadge({ userId }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    // Never show on local dev
    if (isLocalDev || !userId) return;

    let active = true;
    (async () => {
      try {
        const res  = await fetch(`${BACKEND}/idea-validation/usage/${userId}`, { credentials: "include" });
        const json = await res.json();
        // If backend says isDev (e.g. staging NODE_ENV mismatch) or request failed, hide badge
        if (active && json?.success && !json.isDev) setUsage(json);
      } catch (err) {
        console.error("Usage fetch error:", err);
      }
    })();
    return () => { active = false; };
  }, [userId]);

  // Hidden on localhost or when no data yet
  if (isLocalDev || !usage) return null;

  const { used, limit, plan, limitReached } = usage;
  const isUnlimited = limit >= 9999;
  const pct = isUnlimited ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const barColor = limitReached ? "#ef4444" : pct >= 70 ? "#f97316" : "#18a558";

  if (isUnlimited) {
    return (
      <div className="usage-badge" title="Unlimited plan — generate freely">
        <div className="usage-badge-top">
          <span className="usage-badge-label">Plan</span>
          <span className="usage-badge-count" style={{ color: "#18a558" }}>
            {plan || "PRO"} · Unlimited ∞
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="usage-badge"
      title={limitReached ? "Report limit reached — upgrade to Pro" : `${limit - used} report${limit - used !== 1 ? "s" : ""} remaining on your free plan`}
    >
      <div className="usage-badge-top">
        <span className="usage-badge-label">Free reports used</span>
        <span className={`usage-badge-count ${limitReached ? "maxed" : ""}`}>
          {used} / {limit}
        </span>
      </div>
      <div className="usage-badge-bar">
        <div
          className="usage-badge-fill"
          style={{ width: `${pct}%`, background: barColor, opacity: 0.9 }}
        />
      </div>
      {limitReached && (
        <p className="usage-badge-limit-msg">
          Limit reached · <strong>Upgrade to Pro</strong> for unlimited reports
        </p>
      )}
      {!limitReached && pct >= 67 && (
        <p className="usage-badge-limit-msg" style={{ color: "#f97316" }}>
          {limit - used} report{limit - used !== 1 ? "s" : ""} left on free plan
        </p>
      )}
    </div>
  );
}
